(() => {
  const slug = (value) => value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  // Some Shopify collection handles were created before the current menu and
  // do not match the visible label. Keep these aliases in the theme layer so
  // an existing collection is never hidden behind a newly generated 404 URL.
  const collectionAliases = {
    // The legacy `bieres-1` collection was incorrectly reused for juices
    // and contained beer/soda rules. Keep the destination tied to the
    // dedicated, manually curated juice collection.
    'jus-de-fruits': 'jus-de-fruits',
    'choux-et-cruciferes': 'choux-cruciferes',
    'salades-et-feuilles': 'salades-feuilles',
    'soins-du-visage': 'soins-visage',
    'soins-du-corps': 'soins-corps',
    'cheveux': 'soins-cheveux',
    'hygiene-quotidienne': 'hygiene-bien-etre',
    'aromatherapie': 'huiles-essentielles-aromatherapie',
    'entretien': 'produits-entretien',
    'entretien-de-la-maison': 'produits-entretien',
    'linge': 'linge-textiles',
    'cuisine': 'vaisselle-cuisine'
  };

  // A few labels are intentionally reused in different branches of the
  // catalogue. Their destination must follow the branch, otherwise one
  // collection silently replaces another in the dropdown.
  const contextualAliases = {
    'Soda': { 'Limonade bio': 'limonade-bio-canonique' },
    'Biscuits, Gâteaux': { 'Biscuits au chocolat': 'biscuits-au-chocolat-canonique' },
    'Soins du visage': { 'Nettoyants': 'nettoyants-visage' },
    'Entretien de la maison': { 'Nettoyants': 'nettoyants-maison' },
    'Jus de fruits': {
      'Agrumes': 'agrumes-jus',
      'Fruits rouges': 'fruits-rouges-jus'
    },
    'Fruits': {
      'Agrumes': 'agrumes-frais',
      'Fruits rouges': 'fruits-rouges-frais'
    },
    'Plat cuisiné': { 'Tofu et seitan': 'tofu-seitan' },
    'Viandes et alternatives': { 'Tofu et seitan': 'tofu-seitan-frais' },
    'Jardin et extérieur': {
      'Graines': 'graines-jardin',
      'Accessoires': 'accessoires-jardin'
    },
    'Livres': { 'Cuisine': 'livres-cuisine' },
    'Mode': { 'Accessoires': 'accessoires-mode' },
    'Seconde main': {
      'Maison': 'seconde-main-maison',
      'Mode': 'seconde-main-mode'
    }
  };

  const collectionUrl = (label, parentLabel = '') => {
    const handle = slug(label);
    return `/collections/${contextualAliases[parentLabel]?.[label] || collectionAliases[handle] || handle}`;
  };

  const resolveItemUrl = (item, parentLabel = '') => {
    if (parentLabel === 'Rayons' && item.label === 'Frais') return '/collections/frais';
    if (parentLabel === 'Frais' && item.label === 'Fruits') return '/collections/fruits-frais';
    if (parentLabel === 'Frais' && item.label === 'Crèmerie') return '/collections/cremerie-bio';
    if (parentLabel === 'Épicerie salée' && item.label === 'Condiment, vinaigre') return '/collections/condiments-bio';
    if (parentLabel === 'Entretien' && item.label === 'Entretien de la maison') return '/collections/entretien-maison-bio';
    return item.url || collectionUrl(item.label, parentLabel);
  };

  const init = (root) => {
    const dataNode = root.querySelector('[data-rayons-data]');
    const columns = root.querySelector('[data-rayons-menu]');
    if (!dataNode || !columns || columns.dataset.ready) return;
    columns.dataset.ready = 'true';
    let data;
    try { data = JSON.parse(dataNode.textContent); } catch { return; }

    const alignPanel = () => {
      const trigger = root.closest('.custom-rayons-menu')?.querySelector(':scope > .menu-list__link');
      if (!trigger) return;
      const triggerRect = trigger.getBoundingClientRect();
      const submenu = root.closest('.menu-list__submenu');
      const submenuLeft = submenu?.getBoundingClientRect().left || 0;
      const viewportPadding = 16;
      const panelWidth = root.getBoundingClientRect().width || 0;
      const viewportLeft = Math.max(
        viewportPadding,
        Math.min(triggerRect.left, window.innerWidth - panelWidth - viewportPadding)
      );
      root.style.setProperty('margin-left', `${viewportLeft - submenuLeft}px`, 'important');
    };

    root.closest('.custom-rayons-menu')?.addEventListener('pointerenter', alignPanel);
    root.closest('.custom-rayons-menu')?.addEventListener('focusin', alignPanel);
    window.addEventListener('resize', alignPanel, { passive: true });
    alignPanel();

    const clearAfter = (level) => [...columns.querySelectorAll('[data-rayons-level]')].forEach((column) => {
      if (Number(column.dataset.rayonsLevel) > level) column.remove();
    });

    const renderColumn = (items, level, parentLabel) => {
      clearAfter(level - 1);
      const column = document.createElement('ul');
      column.className = `custom-rayons-column${level === 0 ? ' custom-rayons-column--root' : ''} list-unstyled`;
      column.dataset.rayonsLevel = String(level);
      column.setAttribute('aria-label', parentLabel || 'Rayons');

      Object.entries(items || {}).forEach(([key, item]) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'custom-rayons-item';
        link.href = resolveItemUrl(item, parentLabel);
        link.dataset.rayonsKey = key;
        link.innerHTML = `<span>${item.label}</span>${item.children ? '<span class="custom-rayons-arrow" aria-hidden="true">›</span>' : ''}`;
        li.appendChild(link);

        if (item.children) {
          const show = () => {
            column.querySelectorAll('.custom-rayons-item').forEach((entry) => entry.removeAttribute('aria-current'));
            link.setAttribute('aria-current', 'true');
            const childItems = Array.isArray(item.children)
              ? Object.fromEntries(item.children.map((child) => {
                const childItem = typeof child === 'string' ? { label: child } : child;
                return [slug(childItem.label), childItem];
              }))
              : item.children;
            renderColumn(childItems, level + 1, item.label);
          };
          link.addEventListener('pointerenter', show);
          link.addEventListener('focus', show);
        } else {
          link.addEventListener('pointerenter', () => clearAfter(level));
          link.addEventListener('focus', () => clearAfter(level));
        }
        column.appendChild(li);
      });
      columns.appendChild(column);
    };

    renderColumn(data, 0, 'Rayons');
  };

  const scan = () => document.querySelectorAll('.custom-rayons-panel').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
